import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { useTheme } from '../../../hooks/useTheme';
import { makeStyles } from './NetworkDevicesModal.styles';
import Icon from '../../Icon/Icon';
import Badge from './Badge';

export interface NetworkDevice {
  name: string;
  lastSeen: string;
  ipAddress: string;
  isAvailableInLocalNetwork: boolean;
  isCurrentDevice: boolean;
  hasInternetConnection: boolean | null;
  isCDSDevice: boolean;
}

const NetworkDeviceRow: React.FC<NetworkDevice> = ({
  name,
  lastSeen,
  ipAddress,
  isAvailableInLocalNetwork = false,
  hasInternetConnection = false,
  isCurrentDevice = false,
  isCDSDevice = false,
}) => {
  const { translate } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const statusStyles = useMemo(() => {
    if (isAvailableInLocalNetwork === true) {
      return {
        iconName: 'UilCheckCircle',
        iconColor: colors.green,
        borderColor: colors.transparent,
        backgroundColor: colors.elevation1,
      };
    } else if (!isAvailableInLocalNetwork && !hasInternetConnection) {
      return {
        iconName: 'UilExclamationTriangle',
        iconColor: colors.red,
        borderColor: colors.states.negative,
        backgroundColor: colors.redLightest,
      };
    }
    return {
      iconName: 'UilExclamationTriangle',
      iconColor: colors.states.focus,
      borderColor: colors.states.focus,
      backgroundColor: colors.orangeLightest,
    };
  }, [hasInternetConnection, isAvailableInLocalNetwork, colors]);

  const displayName = `${name}${
    isCurrentDevice ? ` (${translate('backOfficeDevices.currentLabel')})` : ''
  }`;

  return (
    <View
      style={[
        styles.rowDevice,
        {
          borderColor: statusStyles.borderColor,
          backgroundColor: statusStyles.backgroundColor,
        },
      ]}
    >
      <View style={styles.text}>
        <Text style={styles.name}>{displayName}</Text>
        <Text style={styles.subtext}>{ipAddress}</Text>
        <Text style={styles.subtext}>{`${translate(
          'common.lastSeen',
        )}: ${lastSeen}`}</Text>
      </View>
      {isCDSDevice && <Badge />}
      <Icon
        size={20}
        name={statusStyles.iconName}
        color={statusStyles.iconColor}
        style={styles.icon}
      />
    </View>
  );
};

export default NetworkDeviceRow;
